import { useState } from "react";
import { checkUnknownError } from "src/utils/errors";
import { logError } from "src/utils/logger";
import { TextBoxErrorText } from "src/components/Common/Form/TextBoxForm";
import { useTranslation } from "react-i18next";
import { BUSINESS_ERROR_CODE, ERROR_CODE } from "src/constants/error";
import { sendAuthOtp, verifyAuth } from "src/api/auth";
import { GRANT_TYPE } from "src/constants/auth";
import { OtpLoginModal } from "./OtpLoginModalV2";
import { OtpButtonsLabel } from "./OtpLogin.types";

type LoginOtpProps = {
  active: boolean;
  method: string;
  form: any;
  code: string;
  field: string;
  lang: string;
  i18nField: string;
  otpField: string;
  errorText: string;
  urlLink: string;
  buttonLabels?: OtpButtonsLabel;
  modalTitle?: string;
  desktopBreakpoint?: boolean;
  onFinishVerifying: (isRegister?: boolean) => void;
};
export const useLoginOtp = ({
  active,
  method,
  form,
  code,
  field,
  lang,
  i18nField,
  otpField,
  errorText,
  onFinishVerifying = () => {},
  buttonLabels,
  modalTitle,
  desktopBreakpoint = false,
}: LoginOtpProps) => {
  const { t } = useTranslation("layout");

  const [isOtpVerified, setIsOtpVerified] = useState<boolean>(false);
  const [isRequestOtpDisable, setIsRequestOtpDisable] = useState<boolean>(true);
  const [otpLoading, setOtpLoading] = useState<boolean>(false);
  const [otpRef, setOtpRef] = useState<string>("");
  const [attempt, setAttempt] = useState<number>(0);
  const [delay, setDelay] = useState<number>(30);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const maxShortDelayAttempt = 5;
  const shortDelay = 30;
  const longDelay = 300;

  const onRequestOtp = async (isResend: boolean) => {
    setOtpLoading(true);
    if (!otpRef || isResend) {
      try {
        const mobileNo = form.getFieldValue(field);
        const res = await sendAuthOtp(mobileNo, lang);
        const { referenceNumber: newOtp } = res;
        setOtpRef(newOtp);
        setAttempt(attempt + 1);
        setDelay(attempt < maxShortDelayAttempt ? shortDelay : longDelay);
      } catch (error) {
        const isUnknownError = checkUnknownError(error);
        if (isUnknownError) {
          logError(`[${[code, field, method]}] send otp fail`, { lang }, error);
        }
        setIsRequestOtpDisable(true);
        form.setFields([
          {
            name: otpField,
            errors: [
              (
                <TextBoxErrorText>
                  {errorText != null
                    ? errorText
                    : t(`modal.mobileNo.otpError.notSuccess`)}
                </TextBoxErrorText>
              ) as unknown as string,
            ],
          },
        ]);
      }
    }
    setOtpLoading(false);
  };

  const onSubmitOtp = async () => {
    setIsLoading(true);
    try {
      const otpNo = form.getFieldValue(otpField);
      const mobileNo = form.getFieldValue(field);
      const payload = {
        otpNo,
        referenceNo: otpRef,
        mobileNo,
        grantType: GRANT_TYPE.OTP,
      };
      const res = await verifyAuth(payload);
      if (!res || res.success === false) {
        form.setFields([
          {
            name: otpField,
            errors: [t(`${i18nField}.otpError.invalid`)],
          },
        ]);
        logError(`[${[field, method]}]submit otp fail`, {
          lang,
          referenceNo: otpRef,
        });
      } else {
        setIsOtpVerified(true);
        onFinishVerifying(!res.isAlreadyRegistered);
      }
      setIsLoading(false);
    } catch (error) {
      const isUnknownError = checkUnknownError(error);
      if (isUnknownError) {
        logError(
          `[${[field, method]}] submit otp fail`,
          { lang, referenceNo: otpRef },
          error
        );
      }
      let errorMessage = t(`${i18nField}.otpError.notSuccess`);
      switch (error.code) {
        case ERROR_CODE.AUTHENTICATION_ERROR:
          errorMessage = t(`${i18nField}.otpError.invalid`);
          break;
        case BUSINESS_ERROR_CODE.EXPIRED:
          errorMessage = t(`${i18nField}.otpError.expired`);
          break;
        case BUSINESS_ERROR_CODE.OTP_EXCEED_RETRY_ATTEMP:
          errorMessage = t(`${i18nField}.otpError.exceedRetryAttemp`);
          break;
        case ERROR_CODE.TECHNICAL_ERROR:
          errorMessage = t(`${i18nField}.otpError.unavailable`);
      }
      form.setFields([
        {
          name: otpField,
          errors: [
            (
              <TextBoxErrorText>{errorMessage}</TextBoxErrorText>
            ) as unknown as string,
          ],
        },
      ]);
      setIsLoading(false);
    }
  };

  const OtpFormSuffix = active && (
    <OtpLoginModal
      otpField={otpField}
      disable={isRequestOtpDisable}
      loading={otpLoading}
      otpRef={otpRef}
      delay={delay}
      onClickSend={onRequestOtp}
      onClickSubmit={onSubmitOtp}
      otpVerified={isOtpVerified}
      target={form.getFieldValue(field)}
      form={form}
      isLoading={isLoading}
      method={method}
      buttonLabels={buttonLabels}
      modalTitle={modalTitle}
      desktopBreakpoint={desktopBreakpoint}
    />
  );

  return {
    OtpFormSuffix,
    setIsRequestOtpDisable,
    isOtpVerified,
    setOtpRef,
    setIsOtpVerified,
  };
};
